import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TQLQueriesApi } from '../api/tql-queries';
import { InterceptedError } from '@/shared/api/intercepted-error';
import type { CreateTQLQuery, TQLQuery, UpdateTQLQuery } from './query';
import { requestError } from '@/shared/ui/notifications';
import { useTranslation } from 'react-i18next';

export const TQL_QUERY = 'TQL_QUERY';

export function useTQLQueries() {
  return useQuery({
    queryKey: [TQL_QUERY, 'list'],
    queryFn: () =>
      TQLQueriesApi.getTQLQueries({ params: { size: -1 } }).then(
        (res) => res.data,
      ),
    cacheTime: 0,
  });
}

export function useTQLQuery(queryName: string) {
  return useQuery({
    queryKey: [TQL_QUERY, queryName],
    queryFn: () => TQLQueriesApi.getTQLQuery(queryName).then((res) => res.data),
    enabled: !!queryName,
    cacheTime: 0,
  });
}

export function useTQLQueryDelete(options?: {
  onSuccess?: (data: string) => void;
  onError?: (data: InterceptedError) => void;
}) {
  const { t } = useTranslation(['translation']);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [TQL_QUERY, 'delete'],
    mutationFn: (queryName: string) =>
      TQLQueriesApi.deleteTQLQuery(queryName).then(() => queryName),
    onSuccess: (queryName) => {
      notifications.show({
        message: t('action.successDeleteMsg'),
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: [TQL_QUERY] });
      queryClient.removeQueries([TQL_QUERY, queryName]);
      options?.onSuccess?.(queryName);
    },
  });
}

export function useTQLQueryCreate(options?: {
  onSuccess?: (data: TQLQuery) => void;
  onError?: (data: InterceptedError) => void;
}) {
  const { onSuccess, onError } = options || {};
  const queryClient = useQueryClient();
  const { t } = useTranslation(['translation']);
  return useMutation({
    mutationKey: [TQL_QUERY, 'create'],
    mutationFn: (query: CreateTQLQuery) =>
      TQLQueriesApi.createTQLQuery(query).then((res) => res?.data),

    onSuccess: (data) => {
      notifications.show({
        message: t('action.successSaveMsg'),
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: [TQL_QUERY] });
      onSuccess?.(data);
    },
    onError: (e: InterceptedError) => {
      requestError(e);
      onError?.(e);
    },
  });
}

export function useTQLQueryUpdate(options?: {
  onSuccess?: (data: TQLQuery) => void;
  onError?: (data: InterceptedError) => void;
}) {
  const { onSuccess, onError } = options || {};
  const { t } = useTranslation(['translation']);
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [TQL_QUERY, 'update'],
    mutationFn: (query: UpdateTQLQuery) =>
      TQLQueriesApi.updateTQLQuery(query.name, query).then((res) => res?.data),
    onSuccess: (data) => {
      notifications.show({
        message: t('action.successSaveMsg'),
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: [TQL_QUERY] });
      onSuccess?.(data);
    },
    onError: (e: InterceptedError) => {
      requestError(e);
      onError?.(e);
    },
  });
}